import dayjs from 'dayjs';
import { getMonthName, getMonthShortName } from './monthlyDataUtils';

export const DEFAULT_FT_RATE = 0.1623;
export const DEFAULT_BILLING_DAY = 15;
export const DEFAULT_SERVICE_FEE = 24.62;
export const DEFAULT_VAT_RATE = 7.0;

export const TARIFF_TIERS = [
    { name: 'Tier 1', range: '1–150 kWh', maxUnits: 150, rate: 3.2484, color: '#3b82f6' },
    { name: 'Tier 2', range: '151–400 kWh', maxUnits: 250, rate: 4.2218, color: '#f59e0b' },
    { name: 'Tier 3', range: '> 400 kWh', maxUnits: Infinity, rate: 4.4217, color: '#ef4444' },
];

/**
 * Format currency in Thai Baht (฿) or custom symbol
 */
export const formatCurrency = (val, symbol = '฿', decimals = 2) => {
    if (val === null || val === undefined || isNaN(val)) return '—';
    const num = parseFloat(val);
    return `${symbol}${num.toLocaleString('en-US', {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
    })}`;
};

/**
 * Format kilowatt-hours (kWh)
 */
export const formatKwh = (val, decimals = 1) => {
    if (val === null || val === undefined || isNaN(val)) return '—';
    const num = parseFloat(val);
    return `${num.toLocaleString('en-US', {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
    })} kWh`;
};

/**
 * Format power in Watts (W) or Kilowatts (kW)
 */
export const formatWatt = (val, decimals = 1) => {
    if (val === null || val === undefined || isNaN(val)) return '—';
    const num = parseFloat(val);
    if (num >= 1000) {
        return `${(num / 1000).toFixed(decimals)} kW`;
    }
    return `${num.toFixed(decimals)} W`;
};

/**
 * Format date string (e.g. 2026-04-15T00:00 -> 15 Apr 2026)
 */
export const formatShortDate = (dateStr) => {
    if (!dateStr) return '—';
    return dayjs(dateStr).format('D MMM YYYY');
};

/**
 * Format billing period range (e.g. 15 Apr 2026 – 15 May 2026)
 */
export const formatBillingPeriod = (start, end) => {
    if (!start || !end) return '—';
    const startFormatted = dayjs(start).format('D MMM YYYY');
    const endFormatted = dayjs(end).format('D MMM YYYY');
    return `${startFormatted} – ${endFormatted}`;
};

/**
 * Calculate billing cycle elapsed percentage and day counts
 */
export const calculateBillingCycleProgress = (periodStart, periodEnd, actualCalculationEnd) => {
    if (!periodStart || !periodEnd) {
        return { progressPercent: 100, elapsedDays: 0, totalDays: 30, isCompleted: true };
    }

    const start = dayjs(periodStart);
    const end = dayjs(periodEnd);
    const actualEnd = actualCalculationEnd ? dayjs(actualCalculationEnd) : dayjs();

    const totalDays = Math.max(1, end.diff(start, 'day'));
    const elapsedDays = Math.min(totalDays, Math.max(0, actualEnd.diff(start, 'day')));
    const progressPercent = Math.min(100, Math.max(0, Math.round((elapsedDays / totalDays) * 100)));

    return {
        progressPercent,
        elapsedDays,
        totalDays,
        isCompleted: progressPercent >= 100,
    };
};

/**
 * Calculate summary KPIs across a list of power cost records
 */
export const calculatePowerCostSummary = (records = []) => {
    if (!records || records.length === 0) {
        return null;
    }

    let totalCost = 0;
    let totalKwh = 0;
    let totalPowerWatt = 0;
    let baseEnergyCost = 0;
    let ftCost = 0;
    let serviceFee = 0;
    let vatAmount = 0;
    let partialCount = 0;
    let completeCount = 0;

    let peakMonthRecord = records[0];

    records.forEach((r) => {
        const cost = parseFloat(r.totalCost) || 0;
        const kwh = parseFloat(r.totalKwh) || 0;
        const watt = parseFloat(r.averagePowerWatt) || 0;
        const baseCost = parseFloat(r.baseEnergyCost) || 0;
        const ft = parseFloat(r.ftCost) || 0;
        const fee = parseFloat(r.serviceFee) || 0;
        const vat = parseFloat(r.vatAmount) || 0;

        totalCost += cost;
        totalKwh += kwh;
        totalPowerWatt += watt;
        baseEnergyCost += baseCost;
        ftCost += ft;
        serviceFee += fee;
        vatAmount += vat;

        if (r.isPartial) {
            partialCount++;
        } else {
            completeCount++;
        }

        if (cost > (parseFloat(peakMonthRecord.totalCost) || 0)) {
            peakMonthRecord = r;
        }
    });

    const averageMonthlyCost = totalCost / records.length;
    const averagePowerWatt = totalPowerWatt / records.length;
    const averageKwh = totalKwh / records.length;

    // Pick latest record
    const latestRecord = [...records].sort((a, b) => (b.month || 0) - (a.month || 0))[0] || records[0];

    return {
        totalCost,
        totalKwh,
        averageMonthlyCost,
        averagePowerWatt,
        averageKwh,
        baseEnergyCost,
        ftCost,
        serviceFee,
        vatAmount,
        recordCount: records.length,
        partialCount,
        completeCount,
        peakMonthRecord,
        latestRecord,
    };
};

/**
 * Prepare data for 12-month Annual Trend Chart (Bar + Line combo)
 */
export const buildAnnualCostChartData = (records = [], selectedYear, t) => {
    // 1 to 12 months array template
    const monthsData = Array.from({ length: 12 }, (_, i) => {
        const monthNum = i + 1;
        return {
            month: monthNum,
            monthName: getMonthName(monthNum, t),
            monthShort: getMonthShortName(monthNum, t),
            totalCost: null,
            totalKwh: null,
            averagePowerWatt: null,
            baseEnergyCost: null,
            ftCost: null,
            isPartial: false,
            hasData: false,
        };
    });

    records.forEach((r) => {
        const mIndex = r.month - 1;
        if (mIndex >= 0 && mIndex < 12) {
            monthsData[mIndex] = {
                ...monthsData[mIndex],
                totalCost: r.totalCost !== undefined ? parseFloat(r.totalCost) : null,
                totalKwh: r.totalKwh !== undefined ? parseFloat(r.totalKwh) : null,
                averagePowerWatt: r.averagePowerWatt !== undefined ? parseFloat(r.averagePowerWatt) : null,
                baseEnergyCost: r.baseEnergyCost !== undefined ? parseFloat(r.baseEnergyCost) : null,
                ftCost: r.ftCost !== undefined ? parseFloat(r.ftCost) : null,
                tier1Cost: r.tier1Cost !== undefined ? parseFloat(r.tier1Cost) : 0,
                tier2Cost: r.tier2Cost !== undefined ? parseFloat(r.tier2Cost) : 0,
                tier3Cost: r.tier3Cost !== undefined ? parseFloat(r.tier3Cost) : 0,
                serviceFee: r.serviceFee !== undefined ? parseFloat(r.serviceFee) : 0,
                vatAmount: r.vatAmount !== undefined ? parseFloat(r.vatAmount) : 0,
                isPartial: Boolean(r.isPartial),
                hasData: true,
                rawRecord: r,
            };
        }
    });

    return monthsData;
};

/**
 * Prepare data for Tiered Tariff Breakdown Chart (Donut / Stacked Bar)
 */
export const buildTariffBreakdownData = (record, t) => {
    if (!record) return [];

    return [
        {
            name: t ? t('common.tier1Cost', 'Tier 1 (1–150 kWh)') : 'Tier 1 (1–150 kWh)',
            units: parseFloat(record.tier1Units) || 0,
            cost: parseFloat(record.tier1Cost) || 0,
            rate: 3.2484,
            color: '#3b82f6',
        },
        {
            name: t ? t('common.tier2Cost', 'Tier 2 (151–400 kWh)') : 'Tier 2 (151–400 kWh)',
            units: parseFloat(record.tier2Units) || 0,
            cost: parseFloat(record.tier2Cost) || 0,
            rate: 4.2218,
            color: '#f59e0b',
        },
        {
            name: t ? t('common.tier3Cost', 'Tier 3 (> 400 kWh)') : 'Tier 3 (> 400 kWh)',
            units: parseFloat(record.tier3Units) || 0,
            cost: parseFloat(record.tier3Cost) || 0,
            rate: 4.4217,
            color: '#ef4444',
        },
        {
            name: t ? t('common.serviceFee', 'Service Fee') : 'Service Fee',
            units: null,
            cost: parseFloat(record.serviceFee) || 0,
            rate: null,
            color: '#8b5cf6',
        },
        {
            name: `${t ? t('common.ftCost', 'Ft Charge') : 'Ft Charge'} (${record.ftRate || DEFAULT_FT_RATE} ฿/kWh)`,
            units: parseFloat(record.totalKwh) || 0,
            cost: parseFloat(record.ftCost) || 0,
            rate: record.ftRate || DEFAULT_FT_RATE,
            color: '#10b981',
        },
        {
            name: `${t ? t('common.vatAmount', 'VAT') : 'VAT'} (${record.vatRate || 7}%)`,
            units: null,
            cost: parseFloat(record.vatAmount) || 0,
            rate: record.vatRate || 7,
            color: '#ec4899',
        },
    ].filter((item) => item.cost > 0);
};

/**
 * Trigger CSV download for power cost records
 */
export const exportPowerCostToCsv = (records = [], selectedYear, t) => {
    if (!records || records.length === 0) return;

    const headers = [
        'Year',
        'Month',
        'Month Name',
        'Period Start',
        'Period End',
        'Status',
        'Total kWh',
        'Average Power (W)',
        'Tier 1 kWh',
        'Tier 1 Cost (฿)',
        'Tier 2 kWh',
        'Tier 2 Cost (฿)',
        'Tier 3 kWh',
        'Tier 3 Cost (฿)',
        'Base Energy Cost (฿)',
        'Service Fee (฿)',
        'Ft Rate (฿/kWh)',
        'Ft Cost (฿)',
        'Total Before VAT (฿)',
        'VAT Rate (%)',
        'VAT Amount (฿)',
        'Total Cost (฿)',
    ];

    const rows = records.map((r) => [
        r.year,
        r.month,
        getMonthName(r.month, t),
        r.periodStart || '',
        r.periodEnd || '',
        r.isPartial ? 'Partial (In Progress)' : 'Complete',
        r.totalKwh !== undefined ? r.totalKwh : '',
        r.averagePowerWatt !== undefined ? r.averagePowerWatt : '',
        r.tier1Units !== undefined ? r.tier1Units : '',
        r.tier1Cost !== undefined ? r.tier1Cost : '',
        r.tier2Units !== undefined ? r.tier2Units : '',
        r.tier2Cost !== undefined ? r.tier2Cost : '',
        r.tier3Units !== undefined ? r.tier3Units : '',
        r.tier3Cost !== undefined ? r.tier3Cost : '',
        r.baseEnergyCost !== undefined ? r.baseEnergyCost : '',
        r.serviceFee !== undefined ? r.serviceFee : '',
        r.ftRate !== undefined ? r.ftRate : '',
        r.ftCost !== undefined ? r.ftCost : '',
        r.totalBeforeVat !== undefined ? r.totalBeforeVat : '',
        r.vatRate !== undefined ? r.vatRate : '',
        r.vatAmount !== undefined ? r.vatAmount : '',
        r.totalCost !== undefined ? r.totalCost : '',
    ]);

    const csvContent = [headers.join(','), ...rows.map((row) => row.join(','))].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `electricity_power_cost_${selectedYear || 'all'}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
};
